import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  initDeepLinkListener,
  parseDeepLink,
  handleInvite,
  type DeepLinkResult,
} from '@/utils/deepLinks';

/**
 * Hook to handle deep links in native apps and web
 * Automatically navigates to the correct route and handles auth callbacks
 */
export function useDeepLinks() {
  const navigate = useNavigate();
  const webUrlProcessed = useRef(false);

  const processDeepLink = useCallback(
    async (result: DeepLinkResult) => {
      console.log('Processing deep link:', result);

      const HASH_TOKEN_KEYS = new Set([
        'access_token',
        'refresh_token',
        'expires_in',
        'expires_at',
        'token_type',
        'provider_token',
        'provider_refresh_token',
      ]);

      const splitQueryAndHash = (params: Record<string, string>) => {
        const query = new URLSearchParams();
        const hash = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (HASH_TOKEN_KEYS.has(key)) {
            hash.set(key, value);
          } else {
            query.set(key, value);
          }
        });
        const queryStr = query.toString();
        const hashStr = hash.toString();
        return {
          search: queryStr ? `?${queryStr}` : '',
          hash: hashStr ? `#${hashStr}` : '',
        };
      };

      switch (result.type) {
        case 'auth_confirm': {
          // AuthConfirmPage owns the verifyOtp / exchange logic.
          if (window.location.pathname.startsWith('/auth/confirm')) {
            break;
          }
          const { search, hash } = splitQueryAndHash(result.params);
          navigate(`/auth/confirm${search}${hash}`, { replace: true });
          break;
        }

        case 'invite': {
          const inviteResult = handleInvite(result.params);
          // Always navigate to invite page - it handles both authenticated and unauthenticated users
          // The InvitePage shows invite details first and then prompts for login if needed
          navigate(inviteResult.redirectTo, { replace: true });
          break;
        }

        case 'oauth_callback': {
          // Skip if already on the callback page to prevent infinite loops
          if (window.location.pathname.startsWith('/auth/callback')) {
            console.log('Already on auth callback page, skipping deep link navigation');
            break;
          }
          // Preserve hash tokens as hash and non-token params as query string,
          // so AuthCallbackPage's hash and PKCE-code readers still find them.
          const { search, hash } = splitQueryAndHash(result.params);
          navigate(`/auth/callback${search}${hash}`, { replace: true });
          break;
        }

        case 'generic': {
          // Navigate to the path from the deep link
          if (result.path && result.path !== '/') {
            navigate(result.path, { replace: true });
          }
          break;
        }

        case 'error': {
          console.error('Deep link error:', result.error);
          navigate('/login?error=invalid_link', { replace: true });
          break;
        }
      }
    },
    [navigate]
  );

  useEffect(() => {
    const cleanup = initDeepLinkListener(processDeepLink);

    // Web initial load: /auth/confirm has its own route that runs the
    // verifyOtp/PKCE/hash logic itself, so we only need to help with
    // /invite/<token> which relies on the hook to stash the token.
    if (typeof window !== 'undefined' && !webUrlProcessed.current) {
      const url = window.location.href;
      const pathname = window.location.pathname;
      if (pathname.startsWith('/invite/')) {
        webUrlProcessed.current = true;
        const result = parseDeepLink(url);
        processDeepLink(result);
      }
    }

    return cleanup;
  }, [processDeepLink]);
}
